import { ApolloLink, ApolloClient, InMemoryCache, ApolloProvider, defaultDataIdFromObject } from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';
import fetch from 'isomorphic-fetch';
import * as React from 'react';
import { ErrorLink } from '@apollo/client/link/error';
import TimeoutLink from 'apollo-link-timeout';
import { RetryLink } from '@apollo/client/link/retry';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

var pageIdHandler = function (pageStore) { return function (operation, forward) {
    if (pageStore && pageStore.pageId) {
        operation.setContext(function (_a) {
            var headers = _a.headers;
            return ({
                headers: __assign(__assign({}, headers), { 'x-page-id': pageStore.pageId }),
            });
        });
    }
    return forward(operation);
}; };
var pageIdLink = function (pageStore) {
    return new ApolloLink(pageIdHandler(pageStore));
};

var GRAPHQL_ERROR_EVENT = new SystemEvent(SystemEventLevel.ERROR, 'APOLLO_GRAPHQL_ERROR');
var NETWORK_ERROR_EVENT = new SystemEvent(SystemEventLevel.ERROR, 'APOLLO_NETWORK_ERROR');
var errorHandler = function (logger, __EXPERIMENTAL__browser_reload_on_auth_error) {
    return function (_a) {
        var graphQLErrors = _a.graphQLErrors, networkError = _a.networkError, operation = _a.operation;
        var operationName = (operation && operation.operationName) || 'unknown';
        if (graphQLErrors) {
            graphQLErrors.forEach(function (error) {
                var message = error.message, extensions = error.extensions;
                logger.logEvent(GRAPHQL_ERROR_EVENT, message, {
                    operation: operationName,
                });
                if (typeof window !== 'undefined' &&
                    __EXPERIMENTAL__browser_reload_on_auth_error &&
                    // experience-api-partner-central returns 'UNAUTHENTICATED' or 'FORBIDDEN'
                    ((extensions === null || extensions === void 0 ? void 0 : extensions.code) === 'UNAUTHENTICATED' || (extensions === null || extensions === void 0 ? void 0 : extensions.code) === 'FORBIDDEN')) {
                    window.location.reload();
                }
            });
        }
        if (networkError) {
            logger.logEvent(NETWORK_ERROR_EVENT, networkError.message, { operation: operationName });
        }
    };
};
var errorLink = function (logger, __EXPERIMENTAL__browser_reload_on_auth_error) {
    return new ErrorLink(errorHandler(logger, __EXPERIMENTAL__browser_reload_on_auth_error));
};

function getFileData(loadFn, logger, systemEvent) {
    var data = {};
    try {
        data = loadFn();
    }
    catch (error) {
        logger.logEvent(systemEvent, error);
    }
    return data;
}

var GRAPHQL_RETRY_EVENT = new SystemEvent(SystemEventLevel.INFO, 'GRAPHQL_RETRY_EVENT');
var GRAPHQL_MAX_RETRY_EVENTS_REACHED_EVENT = new SystemEvent(SystemEventLevel.ERROR, 'GRAPHQL_MAX_RETRY_EVENTS_REACHED');
var retryLinkOptions = function (logger, strategy) {
    var DEFAULT_INITIAL_DELAY = 300;
    var DEFAULT_MAX_RETRIES = 5;
    var initialDelay = strategy && strategy.initialDelay ? strategy.initialDelay : DEFAULT_INITIAL_DELAY;
    return {
        delay: {
            initial: initialDelay,
            max: Infinity,
            jitter: true,
        },
        attempts: function (count, operation, error) {
            var blacklist = strategy && strategy.blacklist ? strategy.blacklist : [];
            if (blacklist.includes(operation.operationName)) {
                return false;
            }
            var maxRetries = strategy && typeof strategy.maxRetries === 'number' ? strategy.maxRetries : DEFAULT_MAX_RETRIES;
            if (count < maxRetries) {
                logger.logEvent(GRAPHQL_RETRY_EVENT, "retry=".concat(count, " max_retry=").concat(maxRetries, " operation=").concat(operation.operationName));
            }
            else {
                logger.logEvent(GRAPHQL_MAX_RETRY_EVENTS_REACHED_EVENT, "max_retry=".concat(maxRetries, " operation=").concat(operation.operationName));
            }
            return !!error && count < maxRetries;
        },
    };
};
var retryLink = function (logger, strategy) {
    return new RetryLink(retryLinkOptions(logger, strategy));
};

var APOLLO_POSSIBLE_TYPES_CLIENT_ERROR = new SystemEvent(SystemEventLevel.ERROR, 'APOLLO_POSSIBLE_TYPES_CLIENT_ERROR');
var APOLLO_TYPE_POLICIES_CLIENT_ERROR = new SystemEvent(SystemEventLevel.ERROR, 'APOLLO_TYPE_POLICIES_CLIENT_ERROR');
function ApolloPlugin(pluginOptions) {
    if (pluginOptions === void 0) { pluginOptions = {}; }
    var browserTimeout = pluginOptions.browserTimeout, ssrForceFetchDelay = pluginOptions.ssrForceFetchDelay, retryStrategy = pluginOptions.retryStrategy, usePossibleTypes = pluginOptions.usePossibleTypes, __EXPERIMENTAL__browser_reload_on_auth_error = pluginOptions.__EXPERIMENTAL__browser_reload_on_auth_error;
    return {
        name: 'apollo',
        onBrowserHydrateRenderContext: function (context, next) {
            var hydratedState = context.hydratedState, logger = context.logger, getAppData = context.getAppData, setAppData = context.setAppData;
            var apolloState = hydratedState.apolloState, clientInfo = hydratedState.clientInfo;
            var stores = getAppData().stores;
            var page = (stores && stores.page) || {};
            // retrieve possible types to be passed into the in memory cache in Apollo Client 3
            var possibleTypes = {};
            if (usePossibleTypes && typeof process.env.GRAPHQL_possible_types__client !== 'undefined') {
                possibleTypes = getFileData(function () { return require(process.env.GRAPHQL_possible_types__client); }, logger, APOLLO_POSSIBLE_TYPES_CLIENT_ERROR);
            }
            // retrieve type policies to be passed into the in memory cache in Apollo Client 3
            var typePolicies = getFileData(function () { return require(process.env.GRAPHQL_type_policies__client); }, logger, APOLLO_TYPE_POLICIES_CLIENT_ERROR);
            var links = [
                errorLink(logger, __EXPERIMENTAL__browser_reload_on_auth_error),
                browserTimeout ? new TimeoutLink(browserTimeout) : null,
                retryStrategy ? retryLink(logger, retryStrategy) : null,
                pageIdLink(page),
                new BatchHttpLink({
                    uri: "".concat(page.baseUrl || '', "/graphql"),
                    headers: {
                        'Client-Info': clientInfo,
                    },
                    credentials: 'same-origin',
                    batchKey: function (operation) { return operation.getContext().batchKey || operation.operationName; },
                    fetch: fetch,
                }),
            ].filter(Boolean);
            var cacheOptions = {
                dataIdFromObject: function (responseObject) {
                    return defaultDataIdFromObject(responseObject);
                },
            };
            if (possibleTypes && Object.keys(possibleTypes).length > 0) {
                cacheOptions.possibleTypes = possibleTypes;
            }
            if (Object.keys(typePolicies).length > 0) {
                cacheOptions.typePolicies = typePolicies;
            }
            var apolloClient = new ApolloClient({
                link: ApolloLink.from(links),
                cache: new InMemoryCache(cacheOptions).restore(apolloState),
                resolvers: {},
                ssrForceFetchDelay: ssrForceFetchDelay,
            });
            setAppData({
                apollo: {
                    client: apolloClient,
                },
            });
            return next(context);
        },
        onBrowserWrapRootElement: function (context, next) {
            var getAppData = context.getAppData, setRootElement = context.setRootElement;
            var apollo = getAppData().apollo;
            var client = apollo.client;
            setRootElement(function (RootElement) { return function (props) { return (React.createElement(ApolloProvider, { client: client },
                React.createElement(RootElement, null, props.children))); }; });
            return next(context);
        },
    };
}

export { ApolloPlugin as default };
//# sourceMappingURL=browser.js.map
